import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { formFieldStyles, labelStyles } from "../../styles/formStyles";
import { getAllPincodes, createPincode, updatePincode, deletePincode } from "../../services/DeliverySettings";
import { IPincodeRequestDTO, IPincodeResponseDTO } from "../../interfaces/DeliverySettings";
import { useAuth } from "../../auth/AuthProvider";

interface DeliveryPincodesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (pincodes: string[]) => void;
}

interface PincodeItem {
  id?: number;
  pincode: string;
  isNew?: boolean;
  isDeleted?: boolean;
}

const DeliveryPincodesDialog = ({ open, onClose, onSave }: DeliveryPincodesDialogProps) => {
  const [pincodes, setPincodes] = useState<PincodeItem[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;

  useEffect(() => {
    if (open) {
      fetchPincodes();
    }
  }, [open]);

  const fetchPincodes = async () => {
    try {
      setLoading(true);
      const response = await getAllPincodes();
      if (response && Array.isArray(response) && response.length > 0) {
        setPincodes(response.map((item: IPincodeResponseDTO) => ({
          id: item.id,
          pincode: item.pincode,
          isNew: false,
          isDeleted: false
        })));
      } else {
        // If no data, show one empty field
        setPincodes([{ pincode: "", isNew: true, isDeleted: false }]);
      }
    } catch (error) {
      console.error("Error fetching pincodes:", error);
      setSnackbarMessage("Failed to load pincodes");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      // If error, show one empty field
      setPincodes([{ pincode: "", isNew: true, isDeleted: false }]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPincode = () => {
    setPincodes([...pincodes, { pincode: "", isNew: true, isDeleted: false }]);
  };

  const handleRemovePincode = (index: number) => {
    const pincode = pincodes[index];
    if (pincode.isNew) {
      // Remove new items immediately
      setPincodes(pincodes.filter((_, i) => i !== index));
    } else {
      // Mark existing items for deletion
      const updated = [...pincodes];
      updated[index] = { ...updated[index], isDeleted: true };
      setPincodes(updated);
    }
  };

  const handlePincodeChange = (index: number, value: string) => {
    const updated = [...pincodes];
    updated[index] = { ...updated[index], pincode: value };
    setPincodes(updated);
    if (errors[index]) {
      setErrors(prev => { const e = { ...prev }; delete e[index]; return e; });
    }
  };

  const validatePincode = (pincode: string) => {
    return /^\d{6}$/.test(pincode);
  };

  const handleSave = async () => {
    if (!WAREHOUSE_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    // Validate all pincodes
    const visibleList = pincodes.filter(p => !p.isDeleted);
    const newErrors: Record<number, string> = {};
    const seen = new Map<string, number>();
    visibleList.forEach((p, index) => {
      if (!p.pincode.trim()) {
        newErrors[index] = "Pincode is required";
      } else if (!validatePincode(p.pincode)) {
        newErrors[index] = "Must be 6 digits";
      } else if (seen.has(p.pincode)) {
        newErrors[index] = "Pincode already exists";
      } else {
        seen.set(p.pincode, index);
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const validPincodes = visibleList;

    try {
      setLoading(true);
      
      // Handle deletions
      const toDelete = pincodes.filter(p => p.isDeleted && p.id);
      for (const item of toDelete) {
        await deletePincode(item.id!);
      }

      // Handle new pincodes
      const toCreate = pincodes.filter(p => p.isNew && !p.isDeleted && p.pincode.trim());
      for (const item of toCreate) {
        const dto: IPincodeRequestDTO = {
          pincode: item.pincode,
          warehouseId: WAREHOUSE_ID
        };
        await createPincode(dto);
      }

      // Handle updates
      const toUpdate = pincodes.filter(p => !p.isNew && !p.isDeleted && p.id);
      for (const item of toUpdate) {
        const dto: IPincodeRequestDTO = {
          pincode: item.pincode,
          warehouseId: WAREHOUSE_ID,
          id: item.id
        };
        await updatePincode(item.id!, dto);
      }

      setSnackbarMessage("Pincodes saved successfully");
      setSnackbarSeverity("success");
      setShowSnackbar(true);
      
      // Call onSave callback if provided
      if (onSave) {
        const savedPincodes = validPincodes.map(p => p.pincode);
        onSave(savedPincodes);
      }
      
      setTimeout(() => {
        onClose();
      }, 1000);
      
    } catch (error: any) {
      console.error("Error saving pincodes:", error);
      setSnackbarMessage(error?.message || "Failed to save pincodes");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPincodes([]);
    onClose();
  };

  const visiblePincodes = pincodes.filter(p => !p.isDeleted);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth 
      PaperProps={{ sx: { borderRadius: "12px" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 600, color: "text.primary" }}>Delivery Pincodes</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "grey.700", mt: 0.5 }}>
            Add delivery pins for directly deliver to the customer
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ position: "absolute", right: 16, top: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{
          pt: 2,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#94A3B8" },
          "&::-webkit-scrollbar-button": { display: "none" },
        }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography component="label" sx={labelStyles}>Delivery Pins</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddPincode}
            disabled={loading}
            sx={{ textTransform: "none", fontSize: "14px" }}
          >
            Add Delivery Pin
          </Button>
        </Box>
        
        {loading && visiblePincodes.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {visiblePincodes.map((pincodeItem, index) => (
              <Box key={pincodeItem.id || `new-${index}`}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography component="label" sx={labelStyles}>
                    PIN Code {index + 1}
                  </Typography>
                  <IconButton 
                    onClick={() => handleRemovePincode(index)} 
                    size="small"
                    disabled={loading}
                  >
                    <Box component="img" src="/delete.svg" alt="Delete" sx={{ width: "20px", height: "20px" }} />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter 6-digit pincode"
                  value={pincodeItem.pincode}
                  onChange={(e) => handlePincodeChange(index, e.target.value)}
                  disabled={loading}
                  error={!!errors[index]}
                  helperText={errors[index] || ""}
                  sx={formFieldStyles}
                />
              </Box>
            ))}
            {visiblePincodes.length === 0 && !loading && (
              <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 2 }}>
                <Typography color="text.secondary">Click "Add Delivery Pin" to add pincodes.</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <Divider sx={{ ml: 1, mr: 1 }} />
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            padding: "8px 16px",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            padding: "8px 40px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            },
          }}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
export default DeliveryPincodesDialog;
